import * as React from 'react';
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import VerticalAlignTop from '@mui/icons-material/VerticalAlignTop';
import VerticalAlignCenter from '@mui/icons-material/VerticalAlignCenter';
import VerticalAlignBottom from '@mui/icons-material/VerticalAlignBottom';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import TextFormatIcon from '@mui/icons-material/TextFormat';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup, {
    toggleButtonGroupClasses,
} from '@mui/material/ToggleButtonGroup';
import { ColorPicker, useColor } from "react-color-palette";
import { checkBit } from '../utils/util';
import Constants from '../config/constants';
import "react-color-palette/css";

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    [`& .${toggleButtonGroupClasses.grouped}`]: {
        border: 0,
        borderRadius: theme.shape.borderRadius,
        [`&.${toggleButtonGroupClasses.disabled}`]: {
            border: 0,
        },
    }
}));

export default function FontStyleToggleButtonGroup({ fontColor, fontStyle, onFontStyleUpdate, onFontColorUpdate }) {
    const [formats, setFormats] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [alignment, setAlignment] = useState(Constants.STYLE_ALIGN_CENTER);
    const [verticalAlignment, setVerticalAlignment] = useState(Constants.STYLE_VERTICAL_ALIGN_CENTER);
    const [color, setColor] = useColor(fontColor.hex);

    // eslint-disable-next-line
    useEffect(() => bootstrapFontStyle(fontStyle), [fontStyle]);

    const handleFormat = (event, newFormats) => {
        setFormats(newFormats);
        onFontStyleUpdate(newFormats
            .concat(alignment)
            .concat(verticalAlignment)
            .reduce((a, b) => a | b, 0));
        onFontColorUpdate(color);
    };

    const handleAlignment = (event, newAlignment) => {
        if (newAlignment == null) {
            return;
        }
        setAlignment(newAlignment);
        onFontStyleUpdate(formats
            .concat(verticalAlignment)
            .concat(newAlignment)
            .reduce((a, b) => a | b, 0));
        onFontColorUpdate(color);
    };

    const handleVerticalAlignment = (event, newVerticalAlignment) => {
        if (newVerticalAlignment == null) {
            return;
        }
        setVerticalAlignment(newVerticalAlignment);
        onFontStyleUpdate(formats
            .concat(alignment)
            .concat(newVerticalAlignment)
            .reduce((a, b) => a | b, 0));
        onFontColorUpdate(color);
    };

    const handleColor = (color) => {
        onFontColorUpdate(color);
        onFontStyleUpdate(formats
            .concat(alignment)
            .concat(verticalAlignment)
            .reduce((a, b) => a | b, 0));
    };

    const bootstrapFontStyleFormat = (fontStyle) => {
        if (checkBit(fontStyle, Constants.FONT_STYLE_BOLD)) {
            setFormats([...formats, Constants.FONT_STYLE_BOLD]);
        }

        if (checkBit(fontStyle, Constants.FONT_STYLE_ITALIC)) {
            setFormats([...formats, Constants.FONT_STYLE_ITALIC]);
        }

        if (checkBit(fontStyle, Constants.FONT_STYLE_UNDERLINE)) {
            setFormats([...formats, Constants.FONT_STYLE_UNDERLINE]);
        }
    };

    const bootstrapFontStyleAlignment = (fontStyle) => {
        if (checkBit(fontStyle, Constants.STYLE_ALIGN_CENTER)) {
            setAlignment(Constants.STYLE_ALIGN_CENTER);
            return;
        }

        if (checkBit(fontStyle, Constants.STYLE_ALIGN_LEFT)) {
            setAlignment(Constants.STYLE_ALIGN_LEFT);
            return;
        }

        if (checkBit(fontStyle, Constants.STYLE_ALIGN_RIGHT)) {
            setAlignment(Constants.STYLE_ALIGN_RIGHT);
            return;
        }
    };

    const bootstrapFontStyleVerticalAlignment = (fontStyle) => {
        if (checkBit(fontStyle, Constants.STYLE_VERTICAL_ALIGN_CENTER)) {
            setVerticalAlignment(Constants.STYLE_VERTICAL_ALIGN_CENTER);
            return;
        }

        if (checkBit(fontStyle, Constants.STYLE_VERTICAL_ALIGN_TOP)) {
            setVerticalAlignment(Constants.STYLE_VERTICAL_ALIGN_TOP);
            return;
        }

        if (checkBit(fontStyle, Constants.STYLE_VERTICAL_ALIGN_BOTTOM)) {
            setVerticalAlignment(Constants.STYLE_VERTICAL_ALIGN_BOTTOM);
            return;
        }
    };

    const bootstrapFontStyle = (fontStyle) => {
        bootstrapFontStyleFormat(fontStyle);
        bootstrapFontStyleAlignment(fontStyle);
        bootstrapFontStyleVerticalAlignment(fontStyle);
    }

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        handleColor(color);
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;


    return (
        <div>
            <Paper
                elevation={0}
                sx={(theme) => ({
                    display: 'flex',
                    border: `1px solid ${theme.palette.divider}`,
                    flexWrap: 'wrap',
                })}>
                <StyledToggleButtonGroup
                    size="large"
                    exclusive
                    value={alignment}
                    onChange={handleAlignment}
                    aria-label="text alignment">
                    <ToggleButton value={Constants.STYLE_ALIGN_LEFT} aria-label="left aligned">
                        <FormatAlignLeftIcon />
                    </ToggleButton>
                    <ToggleButton value={Constants.STYLE_ALIGN_CENTER} aria-label="centered">
                        <FormatAlignCenterIcon />
                    </ToggleButton>
                    <ToggleButton value={Constants.STYLE_ALIGN_RIGHT} aria-label="right aligned">
                        <FormatAlignRightIcon />
                    </ToggleButton>
                </StyledToggleButtonGroup>
                <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1 }} />
                <StyledToggleButtonGroup
                    size="large"
                    exclusive
                    value={verticalAlignment}
                    onChange={handleVerticalAlignment}
                    aria-label="text alignment">
                    <ToggleButton value={Constants.STYLE_VERTICAL_ALIGN_BOTTOM} aria-label="left aligned">
                        <VerticalAlignBottom />
                    </ToggleButton>
                    <ToggleButton value={Constants.STYLE_VERTICAL_ALIGN_CENTER} aria-label="centered">
                        <VerticalAlignCenter />
                    </ToggleButton>
                    <ToggleButton value={Constants.STYLE_VERTICAL_ALIGN_TOP} aria-label="right aligned">
                        <VerticalAlignTop />
                    </ToggleButton>
                </StyledToggleButtonGroup>
                <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1 }} />
                <StyledToggleButtonGroup size="large"
                    value={formats}
                    onChange={handleFormat}
                    aria-label="text formatting">
                    <ToggleButton value={Constants.FONT_STYLE_BOLD} aria-label="bold">
                        <FormatBoldIcon />
                    </ToggleButton>
                    <ToggleButton value={Constants.FONT_STYLE_ITALIC} aria-label="italic">
                        <FormatItalicIcon />
                    </ToggleButton>
                    <ToggleButton value={Constants.FONT_STYLE_UNDERLINE} aria-label="underlined">
                        <FormatUnderlinedIcon />
                    </ToggleButton>
                </StyledToggleButtonGroup>
                <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1 }} />
                <StyledToggleButtonGroup size="large"
                    aria-label="text formatting">
                    <Button aria-describedby={id} variant="text" onClick={handleClick}>
                        <TextFormatIcon />
                        <Box
                            sx={{
                                marginLeft: 1,
                                width: 18,
                                height: 18,
                                borderRadius: 1,
                                bgcolor: color.hex,
                            }}
                        />
                    </Button>
                    <Popover
                        id={id}
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                    >
                        <ColorPicker color={color} onChangeComplete={handleColor} onChange={setColor} />
                    </Popover>
                </StyledToggleButtonGroup>
            </Paper>
        </div>
    );
}

FontStyleToggleButtonGroup.propTypes = {
    fontColor: PropTypes.object,
    fontStyle: PropTypes.number,
    onFontStyleUpdate: PropTypes.func.isRequired,
    onFontColorUpdate: PropTypes.func.isRequired,
};